/**
 * Quick Buy Product Renderer
 * Handles product row rendering from GraphQL data
 */

class QuickBuyProductRenderer {
	constructor(tableBody) {
		this.tableBody = tableBody;
		this.templateId = 'ProductRowTemplate';
	}

	/**
	 * Format money from GraphQL
	 */
	formatMoney(amountStr, currencyCode = 'EUR') {
		const amount = parseFloat(amountStr);
		const price = new Intl.NumberFormat('de-DE', {
			style: 'currency',
			currency: currencyCode,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(amount)
		let cleaned = price.replace(/\s/g, '');
		let symbol = cleaned.match(/[^\d.,]+$/)?.[0] || '';
		let amountValue = cleaned.replace(/[^\d.,]/g, '');
		let result = `${symbol}${amountValue}`;

		return result;
	}

	/**
	 * Request an appropriately sized image from the Shopify CDN.
	 * Non-Shopify URLs are returned unchanged.
	 */
	sizedImageUrl(url, width) {
		try {
			const parsed = new URL(url, window.location.origin);
			const host = parsed.hostname;
			const isShopifyCdn = host === 'cdn.shopify.com'
				|| host.endsWith('.shopify.com')
				|| host.endsWith('.shopifycdn.net')
				|| host.endsWith('.myshopify.com');
			if (!isShopifyCdn) return url;
			parsed.searchParams.set('width', String(width));
			return parsed.toString();
		} catch (e) {
			return url;
		}
	}

	/**
	 * Show a neutral placeholder instead of thumbnail + zoom overlay
	 * (product has no image, or its image failed to load).
	 */
	applyImagePlaceholder(imageContainer) {
		if (!imageContainer || imageContainer.classList.contains('qb-image-missing')) return;
		imageContainer.classList.add('qb-image-missing');
		imageContainer.setAttribute('data-image-url', '');
		imageContainer.innerHTML = '<span class="qb-image-placeholder" aria-hidden="true">'
			+ '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">'
			+ '<rect x="3" y="3" width="18" height="18" rx="3"></rect>'
			+ '<circle cx="8.5" cy="8.5" r="1.5"></circle>'
			+ '<path d="M21 15.5l-5-5L5 21"></path>'
			+ '</svg></span>';
	}

	/**
	 * Build product row from template and map data
	 */
	buildProductRow(product) {
		const variant = product.variants.edges[0]?.node;
		if (!variant) return null;

		// Get template
		const template = document.getElementById(this.templateId);
		if (!template) {
			return null;
		}

		// Clone template content
		const row = template.content.cloneNode(true).querySelector('tr');

		// Extract product data
		const productId = product.id?.split('/').pop();
		const variantId = variant.id?.split('/').pop();
		const price = variant.price ? this.formatMoney(variant.price.amount, variant.price.currencyCode) : '€0.00';
		const compareAtPrice = variant.compareAtPrice ? this.formatMoney(variant.compareAtPrice.amount, variant.compareAtPrice.currencyCode) : null;
		const hasNew = product.tags.includes('new_item') || product.tags.includes('new');
		const quantity = variant.quantityAvailable || 0;
		const barcode = variant.barcode || '—';
		const image = product.featuredImage || variant.image || null;
		const imageUrl = image?.url || '';
		const imageAlt = image?.altText || product.title || '';

		// Map data to row
		row.setAttribute('data-compare-at-price', compareAtPrice || '');
		row.setAttribute('data-net-price', price);
		row.setAttribute('data-product-handle', product.handle);
		row.setAttribute('data-available-qty', String(quantity));
		if (variantId) row.setAttribute('data-variant-id', variantId);

		// NEW badge
		const newBadgeContainer = row.querySelector('.qb-new-badge-container');
		if (hasNew) {
			const newLabel = (window.QB_I18N || {}).new_badge || 'New';
			newBadgeContainer.innerHTML = `<p class="qb-product-name-badge-new">${newLabel}</p>`;
		}

		// PROMO badge (discount percentage)
		const promoBadgeContainer = row.querySelector('.qb-promo-badge-container');
		if (variant.compareAtPrice && variant.price) {
			promoBadgeContainer.innerHTML = `<p class="qb-product-name-badge-promo">%</p>`;
		}

		// Product image
		const imageContainer = row.querySelector('.qb-image-hover-container');
		if (imageUrl) {
			const previewUrl = this.sizedImageUrl(imageUrl, 1200);
			imageContainer.setAttribute('data-image-url', previewUrl);

			const thumbImage = row.querySelector('.qb-product-image');
			thumbImage.src = this.sizedImageUrl(imageUrl, 112);
			thumbImage.alt = imageAlt;
			thumbImage.addEventListener('error', () => this.applyImagePlaceholder(imageContainer), { once: true });

			const hoverImage = row.querySelector('.qb-hover-full-image');
			hoverImage.src = previewUrl;
			hoverImage.alt = imageAlt;
		} else {
			this.applyImagePlaceholder(imageContainer);
		}

		// Product name
		const nameEl = row.querySelector('.qb-product-name');
		nameEl.textContent = product.title;

		// PDP-Link: dezenter ↗ beim Zeilen-Hover, öffnet die Produktseite in neuem Tab
		// (Design "Stock Order Desktop - PDP Link"; Sichtbarkeit steuert CSS .qb-pdp-link)
		if (product.handle) {
			const pdpLabel = (window.QB_I18N || {}).open_pdp || 'Open product page';
			const localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
			const pdpLink = document.createElement('a');
			pdpLink.className = 'qb-pdp-link';
			pdpLink.href = `${localeRoot}products/${product.handle}`;
			pdpLink.target = '_blank';
			pdpLink.rel = 'noopener';
			pdpLink.title = pdpLabel;
			pdpLink.setAttribute('aria-label', `${pdpLabel}: ${product.title}`);
			pdpLink.innerHTML =
				'<svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
				'<path d="M5 2.5h6.5V9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
				'<path d="M11.5 2.5 3 11" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
			pdpLink.addEventListener('click', (e) => e.stopPropagation());
			nameEl.appendChild(pdpLink);
		}

		// Barcode
		row.querySelector('.qb-product-barcode').textContent = barcode;

		// Stock info
		const stockContainer = row.querySelector('.qb-stock-container');
		if (quantity > 0 && quantity < 6) {
			stockContainer.innerHTML = `<p class="stock-info-wrapper stock-info-low-stock">${quantity} </p>`;
		} else if (quantity >= 6) {
			stockContainer.innerHTML = `<p class="stock-info-wrapper stock-info-stock">${quantity}</p>`;
		} else {
			const soldOutLabel = (window.QB_I18N || {}).sold_out || 'Sold out';
			stockContainer.innerHTML = `<p class="stock-info-wrapper stock-info-sold-out">${soldOutLabel}</p>`;
		}

		// Promo price
		const promoPriceContainer = row.querySelector('.qb-promo-price-container');
		if (compareAtPrice) {
			promoPriceContainer.innerHTML = `<p class="compare-at-price-value">${price}</p>`;
		}

		// Net price
		const netPriceElement = row.querySelector('.qb-product-net-price');
		if (compareAtPrice) {
			netPriceElement.textContent = compareAtPrice;
			netPriceElement.style.textDecoration = 'line-through';
		} else {
			netPriceElement.textContent = price;
		}

		// Create and append purchase controls (inline only – no Spark product card)
		const sparkContainer = row.querySelector('.qb-spark-container');
		if (sparkContainer) {
			const purchaseWrap = document.createElement('div');
			purchaseWrap.className = 'qb-inline-purchase';
			const i18n = window.QB_I18N || {};
			const decreaseLabel = i18n.aria_decrease_qty || 'Decrease quantity';
			const increaseLabel = i18n.aria_increase_qty || 'Increase quantity';
			const addToCartLabel = i18n.add_to_cart || 'Add to cart';
			purchaseWrap.innerHTML = `
				<div class="qb-quantity-selector">
					<button type="button" class="qb-quantity-btn" data-action="decrement" aria-label="${decreaseLabel}">-</button>
					<input class="qb-quantity-input" type="number" min="1" value="1" inputmode="numeric" pattern="[0-9]*" data-available-qty="${quantity}" ${quantity > 0 ? `max="${quantity}"` : ''} />
					<button type="button" class="qb-quantity-btn" data-action="increment" aria-label="${increaseLabel}">+</button>
				</div>
				<button type="button" class="qb-add-to-cart-btn" data-variant-id="${variantId || ''}">${addToCartLabel}</button>
				<div class="qb-quantity-message" role="status" aria-live="polite"></div>
			`;

			const qtyInput = purchaseWrap.querySelector('.qb-quantity-input');
			const qtyButtons = purchaseWrap.querySelectorAll('.qb-quantity-btn');
			const addToCartBtn = purchaseWrap.querySelector('.qb-add-to-cart-btn');

			if (quantity > 0) {
				qtyInput.max = String(quantity);
			} else {
				qtyInput.disabled = true;
				qtyButtons.forEach(btn => { btn.disabled = true; });
				addToCartBtn.disabled = true;
			}

			if (!variantId) {
				addToCartBtn.disabled = true;
			}

			sparkContainer.appendChild(purchaseWrap);
		}

		return row;
	}

	/**
	 * Render multiple products
	 */
	renderProducts(products) {
		if (!this.tableBody) {
			return;
		}

		// Clear tbody
		this.tableBody.innerHTML = '';

		if (products.length > 0) {
			// Create document fragment for better performance
			const fragment = document.createDocumentFragment();

			let successCount = 0;
			products.forEach((product, index) => {
				try {
					const row = this.buildProductRow(product);
					if (row) {
						fragment.appendChild(row);
						successCount++;
					}
				} catch (error) {
				}
			});

			// Append all rows at once
			this.tableBody.appendChild(fragment);

			// Note: Discount will be auto-applied by MutationObserver
			// No need to manually call reapplyActiveDiscount here
		} else {
			const noProductsLabel = (window.QB_I18N || {}).no_products_found || 'No products found';
			this.tableBody.innerHTML = `<tr><td colspan="8"><p>${noProductsLabel}</p></td></tr>`;
		}
	}

	/**
	 * No-op for compatibility: Spark product card removed; inline UI only.
	 */
	waitForShadowDOMAndInitialize(onReady) {
		if (onReady && typeof onReady === 'function') onReady();
	}
}

// Export for use in other scripts
window.QuickBuyProductRenderer = QuickBuyProductRenderer;
